import { inject, Service } from '@angular/core';
import { UserContextData } from '@mng/domain';
import { MngHttpService } from '@mng/mng-http';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { VarData } from '@mng/mng-types';

@Service()
/**
 * Provides access to the authenticated user's context and right-based helper checks.
 */
export class MngUserService {
  private readonly httpService = inject(MngHttpService);
  private userContextData: UserContextData;

  /**
   * Loads user context data.
   *
   * This method loads user-related data for the currently logged-in user from the backend.
   */
  private loadUserContextData(preventErrorHandling = false, preventLoadingIndicator = false): Promise<UserContextData> {
    return firstValueFrom(
      this.httpService.get(`auth/api/protected/users/usercontext`, preventErrorHandling, preventLoadingIndicator).pipe(
        map((vardata: VarData<UserContextData>) => vardata.result),
        catchError(() => {
          return of(null);
        })
      )
    );
  }

  /**
   * Returns the currently cached user context data.
   *
   * @returns The user context data, if available.
   */
  getUserContextData(): UserContextData {
    return this.userContextData;
  }

  /**
   * Stores user context data in the service state.
   *
   * @param value - User context data to cache.
   */
  setUserContextData(value: UserContextData): void {
    this.userContextData = value;
  }

  /**
   * Loads and caches the current user context from the backend.
   *
   * @returns The loaded user context data.
   */
  async initUserContext(): Promise<void> {
    const userContext = await this.loadUserContextData(true, true);
    this.userContextData = userContext;
  }

  /**
   * Indicates whether a user context is available.
   *
   * @returns `true` if a user is considered logged in, otherwise `false`.
   */
  isLoggedIn(): boolean {
    return !!this.userContextData;
  }

  /**
   * Checks whether the user has a specific right.
   *
   * @param userRight - The right to check.
   * @returns `true` if the right exists in the user context.
   */
  hasUserRight(userRight: string) {
    const userContextData: UserContextData = this.userContextData;
    if (!userContextData || !userRight) {
      return false;
    }
    return userContextData?.userRights?.includes(userRight);
  }

  /**
   * Checks whether the user has at least one of the provided rights.
   *
   * @param rightsToCheck - Rights to evaluate.
   * @returns `true` if any right matches.
   */
  hasUserAnyRight(...rightsToCheck: string[]) {
    return rightsToCheck.some((right) => this.hasUserRight(right));
  }

  /**
   * Checks whether the user is a Merkur Vermittler.
   *
   * @returns `true` if the user has the Merkur Vermittler role.
   */
  isUserMerkurVermittler(): boolean {
    return this.hasUserRight('ROLE_LFRAY_MERKURVERMITTLER');
  }

  /**
   * Resolves the default home path based on user rights.
   *
   * @returns The target home path for the current user.
   */
  getUserHome(): string {
    if (this.hasUserRight('ROLE_LFRAY_MERKURVERMITTLER')) {
      return '/web/merkur-vertrieb-oesterreich/vertrieb';
    }

    if (this.hasUserRight('ROLE_LFRAY_AT_MAKLER')) {
      return '/group/merkur-vertrieb-oesterreich-mak/makhome';
    }

    if (this.hasUserRight('ROLE_LFRAY_AT_KOOPERATIONSPARTNER')) {
      return '/group/merkur-vertrieb-oesterreich-koop/koophome';
    }

    if (this.hasUserRight('ROLE_SALES_EUROP_ASSISTANCE')) {
      return '/salesclient/europassistance';
    }

    return '/';
  }
}
